import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { companiesApi } from '../services/api/companies';
import type { CargoQuoteRequest } from '../types/quotes';
import { quoteKeys } from './quoteKeys';

export interface UseAdminQuotesReturn {
  quotes: CargoQuoteRequest[];
  loading: boolean;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  handleSearch: (e: React.FormEvent) => void;
  currentPage: number;
  setCurrentPage: (page: number | ((prev: number) => number)) => void;
  itemsPerPage: number;
  setItemsPerPage: (count: number) => void;
  paginatedItems: CargoQuoteRequest[];
  totalPages: number;
  navigateToQuote: (id: number, respond?: boolean) => void;
}

export const useAdminQuotes = (): UseAdminQuotesReturn => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const filters: Record<string, string> = {};
  if (statusFilter) filters.status = statusFilter;
  if (submittedSearch) filters.tracking_number = submittedSearch;

  const { data: rawQuotes, isPending } = useQuery({
    queryKey: quoteKeys.admin.list(filters),
    queryFn: () => companiesApi.getAdminQuotes(filters),
  });

  const quotes = rawQuotes?.cargo_quote_requests ?? [];

  // Reset to page 1 when status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittedSearch(searchQuery);
    setCurrentPage(1);
  };

  const handleSetItemsPerPage = (count: number) => {
    setItemsPerPage(count);
    setCurrentPage(1);
  };

  const navigateToQuote = (id: number, respond?: boolean) => {
    if (respond) {
      navigate(`/admin/quotes/${id}?respond=true`);
    } else {
      navigate(`/admin/quotes/${id}`);
    }
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = quotes.slice(startIndex, endIndex);
  const totalPages = Math.ceil(quotes.length / itemsPerPage);

  return {
    quotes,
    loading: isPending,
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
    handleSearch,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage: handleSetItemsPerPage,
    paginatedItems,
    totalPages,
    navigateToQuote,
  };
};
